import { z } from 'zod';
import { RoleLabel } from './media.js';
/** What Gemini turns a sentence into: exact filters plus residual text for the vector rank. */
export const SearchFilters = z.object({
    facets: z.array(z.object({ key: z.string(), value: z.string() })).default([]),
    roles: z.array(RoleLabel).default([]),
    creators: z.array(z.string()).default([]),
    unusedDays: z.number().int().min(0).nullable().default(null),
    spokenText: z.string().nullable().default(null),
    residual: z.string().default(''),
    /** The lowest star rating asked for, read from the words by starsAsked, never by Gemini; null is the default pool. */
    minStars: z.number().int().min(1).max(5).nullable().default(null),
});
export const SEARCH_FILTERS_JSON_SCHEMA = {
    type: 'object',
    required: ['facets', 'roles', 'creators', 'unusedDays', 'spokenText', 'residual'],
    properties: {
        facets: { type: 'array', items: { type: 'object', required: ['key', 'value'], properties: { key: { type: 'string' }, value: { type: 'string' } } } },
        roles: { type: 'array', items: { type: 'string' } },
        creators: { type: 'array', items: { type: 'string' } },
        unusedDays: { type: 'integer', nullable: true },
        spokenText: { type: 'string', nullable: true },
        residual: { type: 'string' },
    },
};
/**
 * How a ranked search orders footage (Dax, 2026-10-04): meaning above all else. How long since a clip was
 * last published adds a little (its rest, capped at UNUSED_RANKING_CAP_DAYS), its use in drafts hardly
 * anything, and a spread of creators a touch; a clip's badge shows its freshness, and the person decides.
 * Only a clip published inside the brand's cooldown is left out of the Composer's picks.
 */
export const RANK_WEIGHTS = { cosine: 0.85, published: 0.08, drafts: 0.02, diversity: 0.05 };
/** A clip's use in drafts is forgotten after this many days. */
export const DRAFT_USE_CAP_DAYS = 30;
export const TypeaheadKind = z.enum(['facet', 'role', 'creator', 'asset', 'phrase']);
export const TypeaheadHit = z.object({
    kind: TypeaheadKind,
    label: z.string(),
    id: z.string(),
    posterUrl: z.string().nullable(),
    count: z.number().int().nullable(),
});
/**
 * How fresh a clip is, for its badge on the timeline (Dax, 2026-10-04), freshest first: never used; last
 * published long ago; never published but in other drafts; published within 90, 60 or 30 days. "Published"
 * means a post playing it was approved or published, as usage counts everywhere.
 */
export const CLIP_FRESHNESS = ['new', 'rested', 'drafts', 'recent90', 'recent60', 'recent30'];
/** The published bands, in days. */
export const FRESHNESS_BANDS_DAYS = { recent30: 30, recent60: 60, recent90: 90 };
const DAY_MS = 86_400_000;
export function clipFreshness(lastPublishedAt, inOtherDrafts, nowMs) {
    if (lastPublishedAt) {
        const days = (nowMs - new Date(lastPublishedAt).getTime()) / DAY_MS;
        if (days < FRESHNESS_BANDS_DAYS.recent30)
            return 'recent30';
        if (days < FRESHNESS_BANDS_DAYS.recent60)
            return 'recent60';
        if (days < FRESHNESS_BANDS_DAYS.recent90)
            return 'recent90';
        return 'rested';
    }
    return inOtherDrafts ? 'drafts' : 'new';
}
/** Whether `a` is fresher than `b`. */
export function fresherThan(a, b) {
    return CLIP_FRESHNESS.indexOf(a) < CLIP_FRESHNESS.indexOf(b);
}
//# sourceMappingURL=search.js.map